/**
 * LibraryScreen - Content grid with cards
 * Displays all available content in a responsive grid layout
 * 2 columns on phones, 3 columns on tablets/desktops
 */

import { t } from '../i18n/config.js';
import { getProfileContent } from '../services/content.js';
import { ContentCard } from '../components/ContentCard.js';
import * as LikeService from '../services/likes.js';
import { hasActiveAccess, renderAccessDisabled } from '../utils/subscriptionCheck.js';

export class LibraryScreen {
  constructor(container, router, profileData) {
    this.container = container;
    this.router = router;
    this.profileData = profileData;
    this.contentCards = [];
  }

  /**
   * Render library screen
   */
  async render() {
    this.container.innerHTML = '';
    this.container.innerHTML = `<div class="library-screen"></div>`;

    window.showLoader();

    try {
      const contentResponse = await this.loadAllContent();

      // Check subscription access (Req 9.1-9.6)
      if (!hasActiveAccess(contentResponse)) {
        window.hideLoader();
        renderAccessDisabled(this.container);
        return;
      }

      const allContent = contentResponse.modules || contentResponse || [];

      const deviceId = localStorage.getItem('kassie_device_id');
      let likeStates = {};

      if (deviceId) {
        try {
          likeStates = await LikeService.getProfileLikes(this.profileData.id, deviceId);
          console.log('✅ Loaded like states:', likeStates);
        } catch (error) {
          console.error('⚠️ Failed to load like states:', error);
        }
      }

      this.renderGrid(allContent, likeStates);

      // Wait for all card images to load before hiding the loader
      await this.waitForImages();
      window.hideLoader();
      console.log('✅ Library grid rendered (all images loaded)');
    } catch (error) {
      console.error('Error loading library screen:', error);
      window.hideLoader();
      this.renderError();
    }
  }

  /**
   * Load all content
   */
  async loadAllContent() {
    try {
      console.log('📚 Loading all content for profile:', this.profileData.id);
      const content = await getProfileContent(this.profileData.id);
      console.log(`✅ Loaded ${Array.isArray(content) ? content.length : 'N/A'} content items`);
      return content;
    } catch (error) {
      console.error('Failed to load content:', error);
      return [];
    }
  }

  /**
   * Render content as a responsive grid
   */
  renderGrid(contentItems, likeStates = {}) {
    const libraryScreen = this.container.querySelector('.library-screen');

    const gridHTML = `
      <div class="library-grid" id="libraryGrid">
        <!-- Content cards inserted here -->
      </div>
    `;

    libraryScreen.insertAdjacentHTML('beforeend', gridHTML);

    const grid = this.container.querySelector('.library-grid');

    // Filter out sing modules without an audio file (they should be hidden)
    const visibleItems = contentItems.filter(item => {
      if ((item.activityType === 'sing' || item.activityType === 'Sing') && !item.audioUrl) {
        return false;
      }
      return true;
    });

    // Sort by activity type: read (lees) first, then sing
    const activityOrder = { lees: 0, read: 0, sing: 1 };
    visibleItems.sort((a, b) => {
      const orderA = activityOrder[a.activityType?.toLowerCase()] ?? 2;
      const orderB = activityOrder[b.activityType?.toLowerCase()] ?? 2;
      return orderA - orderB;
    });

    const moduleCount = visibleItems.length;
    console.log(`📚 Rendering ${moduleCount} modules in grid`);

    visibleItems.forEach((contentItem, index) => {
      const cardWrapper = document.createElement('div');
      cardWrapper.className = 'library-grid__item';
      grid.appendChild(cardWrapper);

      const isLiked = likeStates[contentItem.id] || false;
      const card = new ContentCard(cardWrapper, contentItem, this.router, this.profileData, isLiked);
      card.render();
      this.contentCards.push(card);
    });

    console.log(`✅ ${moduleCount} cards rendered in grid`);
  }

  /**
   * Render error state
   */
  renderError() {
    this.container.innerHTML = `
      <div class="library-screen">
        <div class="profile-empty">
          <div class="profile-empty-icon">📚</div>
          <p class="profile-empty-message">
            ${t('child.errors.couldNotLoadContent') || 'Could not load content.'}
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Wait for all images in the library grid to load (or fail).
   * Times out after 5 seconds to avoid infinite loader.
   */
  async waitForImages() {
    const images = this.container.querySelectorAll('.library-grid img');
    if (images.length === 0) return;

    const timeout = 5000;
    const imagePromises = Array.from(images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    });

    await Promise.race([
      Promise.all(imagePromises),
      new Promise((resolve) => setTimeout(resolve, timeout))
    ]);
  }

  /**
   * Destroy view
   */
  destroy() {
    this.container.innerHTML = '';
    this.contentCards = [];
  }
}
